import { replicateRxCollection } from 'rxdb/plugins/replication'
import { addWebSocketToReplication } from '../utils/addWebSocketToReplication'
import { awaitInitialReplicationWithTimeout } from '../utils/awaitInitialReplicationWithTimeout'
import { PurgeService } from '../cleanup/purgeService'
import { filterValidConflicts, filterValidPushDocs } from '../utils/pushSafetyUtils'
import type { RxCollection, RxReplicationWriteToMasterRow, WithDeleted } from 'rxdb'
import { fetchWithAuth } from '@/lib/auth/fetchWithAuth'
import { API_URL } from '@/lib/config'

interface PullResponse {
  documents: Array<any>
  checkpoint: {
    updatedAt: number
  }
}

interface PushResponse {
  conflicts?: Array<any>
}

export interface ReplicationConfig<T = any> {
  /** Entity name used in API endpoints and WebSocket channels (e.g., 'tasks', 'comments') */
  entityName: string
  /** Display name for logging (e.g., 'Task', 'Comment') */
  displayName: string
  /** RxDB collection to replicate */
  collection: RxCollection<T>
  /** Workspace environment ID (omit for global entities like plans) */
  envId?: string
  /** Map a server document (from WS or pull) to local RxDB format */
  mapFromServer: (doc: any) => T
  /** Map a local document state to server push format. If omitted, push is disabled (read-only). */
  mapToServer?: (docState: any, envId: string) => any
  /** Whether to use WebSocket for real-time updates (default: true) */
  useWebSocket?: boolean
  /** Whether to filter out old soft-deleted docs on pull (default: true) */
  usePurgeFilter?: boolean
  /** Batch size for pull and push (default: 100) */
  batchSize?: number
  /** API path segment if different from entityName (e.g., 'work-item-types' vs 'workitemtypes') */
  apiEntityName?: string
  /**
   * Optional predicate applied to documents arriving on the WebSocket stream
   * before they reach RxDB. Use for per-user collections that share a
   * workspace-scoped broadcast channel — e.g. drop docs whose `userId`
   * differs from the current user.
   */
  documentFilter?: (doc: any) => boolean
}

/**
 * Generic replication factory that eliminates boilerplate across entity replication files.
 *
 * Handles:
 * - WebSocket stream setup and cleanup
 * - Pull handler with auth, checkpoint, and purge filtering
 * - Push handler with doc validation and conflict resolution
 * - Initial replication await with timeout
 *
 * Usage:
 * ```ts
 * export const setupFooReplication = (collection, envId) =>
 *   createReplication({
 *     entityName: 'foos',
 *     displayName: 'Foo',
 *     collection,
 *     envId,
 *     mapFromServer: (doc) => ({ id: doc.id, ... }),
 *     mapToServer: (state, envId) => ({ id: state.id, envId, ... }),
 *   })
 * ```
 */
export async function createReplication<T = any>({
  entityName,
  displayName,
  collection,
  envId,
  mapFromServer,
  mapToServer,
  useWebSocket = true,
  usePurgeFilter = true,
  batchSize = 100,
  apiEntityName,
  documentFilter,
}: ReplicationConfig<T>) {
  const apiPath = apiEntityName ?? entityName
  let close: (() => void) | undefined
  let stream$: any

  if (useWebSocket) {
    const ws = addWebSocketToReplication(
      entityName,
      mapFromServer,
      documentFilter ? { documentFilter } : undefined,
    )
    stream$ = ws.stream$
    close = ws.close
  }

  const replicationState = replicateRxCollection({
    collection,
    replicationIdentifier: envId ? `${entityName}-${envId}` : entityName,

    pull: {
      async handler(lastCheckpoint: any, pullBatchSize) {
        const lastUpdated = lastCheckpoint?.updatedAt || 0

        const params: Record<string, string> = {
          updatedAt: String(lastUpdated),
          limit: String(pullBatchSize),
        }
        if (envId) params.env = envId

        const response = await fetchWithAuth(
          `${API_URL}/api/sync/${apiPath}/pull?` +
            new URLSearchParams(params),
          {
            headers: { 'Content-Type': 'application/json' },
          },
        )

        if (!response.ok) {
          await response.text()
          throw new Error(`Pull failed: ${response.status} ${response.statusText}`)
        }

        const data: PullResponse = await response.json()

        if (!data.documents) {
          throw new Error('Backend returned invalid format - missing documents array')
        }

        let documents = data.documents.map(mapFromServer)

        if (usePurgeFilter) {
          const purgeCutoff = PurgeService.getPurgeCutoffForCollection()
          documents = documents.filter((doc: any) => {
            return !doc.isDeleted || !doc.deletedOn || doc.deletedOn >= purgeCutoff
          })
        }

        return {
          documents: documents as Array<WithDeleted<T>>,
          checkpoint: data.checkpoint,
        }
      },
      batchSize,
      ...(stream$ ? { stream$: stream$ } : {}),
    },

    push: {
      async handler(docs: Array<RxReplicationWriteToMasterRow<any>>) {
        if (!mapToServer) {
          // Read-only replication
          return []
        }

        const validDocs = filterValidPushDocs(docs, entityName)
        if (validDocs.length === 0) return []

        const documents = validDocs.map((doc) =>
          mapToServer(doc.newDocumentState, envId ?? ''),
        )

        const response = await fetchWithAuth(`${API_URL}/api/sync/${apiPath}/push`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ documents }),
        })

        if (!response.ok) {
          const errorText = await response.text()
          throw new Error(`Push failed: ${response.statusText} - ${errorText}`)
        }

        const result: PushResponse = await response.json()
        return filterValidConflicts(result.conflicts || [])
      },
      batchSize,
    },
  })

  if (close) {
    replicationState.canceled$.subscribe(() => {
      close!()
    })
  }

  await awaitInitialReplicationWithTimeout(replicationState, displayName)

  return replicationState
}
